/**
 * Patient Transfer workflow — backs /clinical-transfers on the backend (PatientTransferController).
 * Ongoing care itself moves, distinct from Referral (patient stays with the referring provider —
 * see modules/clinicalReferrals.ts). ClinVea-network destinations only; external transfer delivery
 * is not built.
 */
import { fetchApi, fetchGetFileApi } from '../client';
import { buildQS } from '../helpers';
import type { PagedResponse } from '../../../types/pagination';
import type {
  ClinicalUrgency, RecordShareCategory, RecordShareSelection, DestinationClinic, DestinationDoctor, PatientConsentMethod,
  ConsentCapture,
} from './clinicalReferrals';
import { documentsApi } from './clinicalReferrals';

export type TransferType = 'CARE_TRANSFER' | 'EMERGENCY_TRANSFER' | 'FACILITY_TRANSFER' | 'PROVIDER_TRANSFER' | 'RECORD_TRANSFER';

export type TransferStatus =
  | 'DRAFT' | 'REQUESTED' | 'EMERGENCY_INITIATED' | 'RECEIVING_FACILITY_CONTACTED' | 'SENT'
  | 'RECEIVED' | 'UNDER_REVIEW' | 'ACCEPTED' | 'DECLINED' | 'TRANSPORT_PENDING' | 'TRANSPORT_ARRANGED'
  | 'PATIENT_DEPARTED' | 'PATIENT_ARRIVED' | 'HANDOFF_COMPLETED' | 'COMPLETED' | 'CANCELLED';

export interface TransferTransport {
  transportType?: string;
  receivingFacilityName?: string;
  receivingFacilityPhone?: string;
  receivingContactPerson?: string;
  receivingFacilityContactedAt?: string;
  transportArrangedAt?: string;
  expectedArrivalAt?: string;
  departedAt?: string;
  arrivedAt?: string;
  vehicleDetails?: string;
  ambulanceContact?: string;
  escortName?: string;
  escortRole?: string;
  escortPhone?: string;
  oxygenRequired?: boolean;
  monitoringRequired?: boolean;
  notes?: string;
}

/** The ISBAR handover: written by the sending team before departure, confirmed by the receiver. */
export interface TransferHandoff {
  vitalsSummary?: string;
  treatmentProvided?: string;
  medicationsAdministered?: string;
  lastMedicationTimes?: string;
  allergies?: string;
  relevantLabs?: string;
  relevantImaging?: string;
  pendingResults?: string;
  linesAndDevices?: string;
  isolationPrecautions?: string;
  codeStatus?: string;
  documentsSent?: string;
  clinicalConcerns?: string;
  sendingProviderId?: string;
  sendingProviderName?: string;
  receivingProviderId?: string;
  receivingProviderName?: string;
  preparedBy?: string;
  preparedAt?: string;
  handoffConfirmedBy?: string;
  handoffConfirmedAt?: string;
}

export interface Transfer {
  id: string;
  transferNumber: string;
  patientId: string;
  patientName?: string;
  patientMobile?: string;
  patientDateOfBirth?: string;
  patientAge?: number;
  patientGender?: string;

  initiatingClinicId: string;
  initiatingClinicName?: string;
  initiatingProviderId?: string;
  initiatingProviderName?: string;

  receivingOrganizationId: string;
  receivingOrganizationName?: string;
  receivingProviderId?: string;
  receivingProviderName?: string;

  transferType: TransferType;
  urgency: ClinicalUrgency;
  reason: string;
  clinicalSummary?: string;
  currentCondition?: string;
  transportType?: string;
  status: TransferStatus;

  admissionId?: string;
  admissionNumber?: string;
  provisionalDiagnosis?: string;
  icdCode?: string;
  patientConsentCaptured: boolean;
  consentMethod?: PatientConsentMethod;
  consentCapturedAt?: string;
  consentNotes?: string;
  againstMedicalAdvice: boolean;
  acceptedWardId?: string;
  acceptedWardName?: string;
  acceptedBedId?: string;
  acceptedBedNumber?: string;
  acceptanceNotes?: string;
  receivingAdmissionId?: string;
  receivingAdmissionNumber?: string;
  sourceDischargedAt?: string;

  requestedAt: string;
  acceptedAt?: string;
  declinedAt?: string;
  departedAt?: string;
  arrivedAt?: string;
  handoffCompletedAt?: string;
  completedAt?: string;
  cancelledAt?: string;

  incoming: boolean;
  allowedNextStatuses: TransferStatus[];
  recordShare?: RecordShareSelection | null;
  transport?: TransferTransport | null;
  handoff?: TransferHandoff | null;
}

export interface TransferDashboard {
  outgoingTotal: number;
  outgoingPendingDecision: number;
  outgoingInTransit: number;
  outgoingCompleted: number;
  incomingTotal: number;
  incomingNeedsReview: number;
  incomingAccepted: number;
  incomingAwaitingHandoff: number;
  emergencyOpen: number;
}

export interface TransferStatusHistoryEntry {
  id: string;
  transferId: string;
  fromStatus: TransferStatus | null;
  toStatus: TransferStatus;
  changedBy: string;
  reason?: string | null;
  changedAt: string;
}

export interface TransferCreateRequest {
  patientId: string;
  initiatingProviderId?: string;
  receivingOrganizationId: string;
  receivingProviderId?: string;
  transferType: TransferType;
  urgency: ClinicalUrgency;
  reason: string;
  clinicalSummary?: string;
  currentCondition?: string;
  transportType?: string;
  /** The sending clinic's active admission being transferred out — optional. */
  admissionId?: string;
  provisionalDiagnosis?: string;
  icdCode?: string;
  /** Required to submit; EMERGENCY_EXCEPTION needs the reason in consentNotes. */
  patientConsentCaptured: boolean;
  consentMethod?: PatientConsentMethod;
  consentNotes?: string;
  againstMedicalAdvice?: boolean;
  recordShareCategories?: RecordShareCategory[];
  recordShareDateFrom?: string;
  recordShareDateTo?: string;
  submit: boolean;
}

export interface TransferTransportRequest {
  transportType?: string;
  receivingFacilityName?: string;
  receivingFacilityPhone?: string;
  receivingContactPerson?: string;
  vehicleDetails?: string;
  ambulanceContact?: string;
  escortName?: string;
  escortRole?: string;
  escortPhone?: string;
  oxygenRequired?: boolean;
  monitoringRequired?: boolean;
  expectedArrivalAt?: string;
  notes?: string;
}

export interface TransferHandoffRequest {
  vitalsSummary?: string;
  treatmentProvided?: string;
  medicationsAdministered?: string;
  lastMedicationTimes?: string;
  allergies?: string;
  relevantLabs?: string;
  relevantImaging?: string;
  pendingResults?: string;
  linesAndDevices?: string;
  isolationPrecautions?: string;
  codeStatus?: string;
  documentsSent?: string;
  clinicalConcerns?: string;
  receivingProviderId?: string;
}

/** Receiving side's acceptance: optional doctor, ward and a bed to hold until admission. */
export interface TransferAcceptRequest {
  receivingProviderId?: string;
  wardId?: string;
  bedId?: string;
  notes?: string;
}

/** Admit the arrived patient (TRANSFER_IN) or link an admission created at the desk. */
export interface TransferAdmitRequest {
  existingAdmissionId?: string;
  localPatientId: string;
  doctorId?: string;
  bedId?: string;
  currentCondition?: string;
  notes?: string;
}

const BASE = '/api/clinical-transfers';

export const clinicalTransfersModule = {
  listOutgoing: (status?: TransferStatus) =>
    fetchApi<Transfer[]>(`${BASE}${buildQS({ status })}`),

  listIncoming: () =>
    fetchApi<Transfer[]>(`${BASE}/incoming`),

  /** One page of outgoing transfers; status and search (number, reason, patient name) run on the server (medopd-docs 112). */
  listOutgoingPage: (params: { status?: TransferStatus; q?: string; page: number; size: number }) =>
    fetchApi<PagedResponse<Transfer>>(`${BASE}${buildQS(params)}`),

  /** One page of incoming transfers, searched on the server (medopd-docs 112). */
  listIncomingPage: (params: { q?: string; page: number; size: number }) =>
    fetchApi<PagedResponse<Transfer>>(`${BASE}/incoming${buildQS(params)}`),

  getDashboard: () =>
    fetchApi<TransferDashboard>(`${BASE}/dashboard`),

  searchDestinationClinics: (q: string) =>
    fetchApi<DestinationClinic[]>(`${BASE}/destinations/clinics${buildQS({ q })}`),

  listDestinationDoctors: (destinationClinicId: string) =>
    fetchApi<DestinationDoctor[]>(`${BASE}/destinations/clinics/${destinationClinicId}/doctors`),

  get: (id: string) =>
    fetchApi<Transfer>(`${BASE}/${id}`),

  getTimeline: (id: string) =>
    fetchApi<TransferStatusHistoryEntry[]>(`${BASE}/${id}/timeline`),

  create: (data: TransferCreateRequest) =>
    fetchApi<Transfer>(BASE, { method: 'POST', body: JSON.stringify(data) }),

  /** `consent` records the patient's consent on a draft saved without it. */
  submit: (id: string, consent?: ConsentCapture) =>
    fetchApi<Transfer>(`${BASE}/${id}/submit`, { method: 'POST', body: JSON.stringify(consent ?? {}) }),

  markFacilityContacted: (id: string, data?: TransferTransportRequest) =>
    fetchApi<Transfer>(`${BASE}/${id}/facility-contacted`, { method: 'POST', body: JSON.stringify(data ?? {}) }),

  accept: (id: string, data?: TransferAcceptRequest) =>
    fetchApi<Transfer>(`${BASE}/${id}/accept`, { method: 'POST', body: JSON.stringify(data ?? {}) }),

  /** A reason is mandatory — the sending team needs it to redirect the patient. */
  decline: (id: string, reason: string) =>
    fetchApi<Transfer>(`${BASE}/${id}/decline`, { method: 'POST', body: JSON.stringify({ reason }) }),

  /** Sending team writes the ISBAR handover before departure (editable until confirmed). */
  prepareHandoff: (id: string, data: TransferHandoffRequest) =>
    fetchApi<Transfer>(`${BASE}/${id}/prepare-handoff`, { method: 'POST', body: JSON.stringify(data) }),

  /** Receiving clinic admits the arrived patient or links an existing admission. */
  admit: (id: string, data: TransferAdmitRequest) =>
    fetchApi<Transfer>(`${BASE}/${id}/admit`, { method: 'POST', body: JSON.stringify(data) }),

  /** The printable transfer summary (PDF) that travels with the patient. */
  getSummary: (id: string) => fetchGetFileApi(`${BASE}/${id}/summary`),

  /** Attachments (imaging, discharge summary, consent form) — both clinics can add and read them. */
  documents: documentsApi(BASE),

  markTransportReady: (id: string, data?: TransferTransportRequest) =>
    fetchApi<Transfer>(`${BASE}/${id}/transport-ready`, { method: 'POST', body: JSON.stringify(data ?? {}) }),

  markDeparted: (id: string) =>
    fetchApi<Transfer>(`${BASE}/${id}/departed`, { method: 'POST' }),

  markArrived: (id: string) =>
    fetchApi<Transfer>(`${BASE}/${id}/arrived`, { method: 'POST' }),

  confirmHandoff: (id: string, data?: TransferHandoffRequest) =>
    fetchApi<Transfer>(`${BASE}/${id}/handoff`, { method: 'POST', body: JSON.stringify(data ?? {}) }),

  complete: (id: string) =>
    fetchApi<Transfer>(`${BASE}/${id}/complete`, { method: 'POST' }),

  cancel: (id: string, reason?: string) =>
    fetchApi<Transfer>(`${BASE}/${id}/cancel`, { method: 'POST', body: JSON.stringify({ reason }) }),

  getRecordShare: (id: string) =>
    fetchApi<RecordShareSelection | null>(`${BASE}/${id}/record-share`),

  setRecordShare: (id: string, data: { categories: RecordShareCategory[]; dateFrom?: string; dateTo?: string }) =>
    fetchApi<RecordShareSelection>(`${BASE}/${id}/record-share`, { method: 'POST', body: JSON.stringify(data) }),
};
